<template>
  <div class="details-container">
    <ResponseMessage :type="messageType">{{ errorMessage }}</ResponseMessage>
    <AppStoreBanner />
    <VideoHero v-if="videoData" :video="videoData"></VideoHero>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import { getPublicVideo, videoKeys } from '@/scripts/api/videoApi';
import { handleApiError } from '@/scripts/shared-utils';
import AppStoreBanner from '@/components/AppStoreBanner.vue';
import ResponseMessage from '@/components/ResponseMessage.vue';
import VideoHero from '@/components/VideoHero.vue';

const route = useRoute();
const shortId = computed(() => route.params.short_id);

const publicVideoQueryKey = computed(() => videoKeys.getPublicVideo(shortId.value));

const { data: videoData, error } = useQuery({
  queryKey: publicVideoQueryKey,
  queryFn: () => getPublicVideo(shortId.value),
  enabled: computed(() => !!shortId.value),
  staleTime: 60*60000, // one hour
  retry: false, // We don't need multiple retries here as links becoming unavailable is a common occurrence.
  onError: (error) => handleApiError(error, 'fetching shared video data')
});

const errorMessage = computed(() => {
  if (!error.value) return null;
  if (error.value.response && error.value.response.status === 404) {
    return 'This link is invalid or expired. Please check with the person who shared it with you.';
  }
  return 'An error occurred while fetching the video.';
});

const messageType = computed(() => error.value ? "error" : "");
</script>

<style scoped>
@import '@/styles/shared-details.css';
</style>
